import { parseISO, isPast, isValid, addMinutes } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { DEFAULT_EVENT_DAYS, MIN_EVENT_DAYS } from './constants'

export function hasExpired(utcDate: string): boolean {
  try {
    return isPast(parseISO(utcDate))
  } catch {
    return false
  }
}

export function createExpiryDate(minutes: number): string {
  return addMinutes(new Date(), minutes).toISOString()
}

export function parseUTCDate(utcDate: string): Date | null {
  try {
    const date = parseISO(utcDate)
    return isValid(date) ? date : null
  } catch {
    return null
  }
}

/** HH:mm → nearest 10 min (e.g. 14:12 → 14:10). */
export function roundToTenMinutes(time: string): string {
  const [hours, minutes] = time.split(':').map(Number)
  const rounded = Math.round(minutes / 10) * 10

  const finalHours = rounded === 60 ? (hours + 1) % 24 : hours
  const finalMinutes = rounded === 60 ? 0 : rounded

  return `${String(finalHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`
}

export function toEventLocalDate(
  utcDate: string,
  eventTimezone: string
): Date | null {
  try {
    return toZonedTime(parseISO(utcDate), eventTimezone)
  } catch {
    return null
  }
}

/** Full calendar days from today (local midnight) to the UTC date's local day. Math.round required for DST (23h nights). Invalid input → NEGATIVE_INFINITY so callers using >= MIN_EVENT_DAYS fail safely. */
export function calendarDaysUntil(utcDateString: string): number {
  if (!utcDateString) return Number.NEGATIVE_INFINITY
  const eventDay = new Date(utcDateString)
  if (isNaN(eventDay.getTime())) return Number.NEGATIVE_INFINITY

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  eventDay.setHours(0, 0, 0, 0)

  return Math.round(
    (eventDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
  )
}

export function getMinEventDate(): string {
  const d = new Date(Date.now() + MIN_EVENT_DAYS * 24 * 60 * 60 * 1000)
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-')
}

/** Default event date/time: DEFAULT_EVENT_DAYS from now, ceiled to 10-min. Round timestamp before extracting parts so we do not get 23:60 and wrong calendar day. */
export function getDefaultEventDateTime(): {
  date: string
  time: string
  dateTime: Date
} {
  const MS_10_MIN = 10 * 60 * 1000
  const dateTime = new Date(
    Math.ceil(
      (Date.now() + DEFAULT_EVENT_DAYS * 24 * 60 * 60 * 1000) / MS_10_MIN
    ) * MS_10_MIN
  )

  return {
    date: [
      dateTime.getFullYear(),
      String(dateTime.getMonth() + 1).padStart(2, '0'),
      String(dateTime.getDate()).padStart(2, '0'),
    ].join('-'),
    time: [
      String(dateTime.getHours()).padStart(2, '0'),
      String(dateTime.getMinutes()).padStart(2, '0'),
    ].join(':'),
    dateTime,
  }
}
